import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface NursingHome {
  id?: number;  // Making id optional since it might be missing in the database response
  center_name: string;
  nh_slug: string;
  description?: string;
  short_description?: string;
  // Address fields
  street_name?: string; // Added for BasicInfoForm
  street_address?: string; // For backward compatibility
  town?: string; // Added for BasicInfoForm and OverviewPage
  city_town?: string; // For backward compatibility
  county?: string; // Added for BasicInfoForm and OverviewPage
  county_name?: string; // For backward compatibility
  county_slug?: string;
  eircode?: string;
  eir_code?: string; // Alternative spelling used in some components
  // Contact information
  phone_number?: string;
  standardized_phone_number?: string;
  email?: string;
  official_website?: string;
  // Images and media
  main_image_url?: string; // Important: This property is used in MediaPage.tsx
  // Location data
  latitude?: number;
  longitude?: number;
  // Facility information
  price_range?: string;
  inspection_rating?: string;
  fair_deal_approved?: boolean;
  last_inspection_date?: string;
  facilities?: string[];
  services?: string[];
  amenities?: string[];
  room_types?: string[];
  visiting_hours?: string; // Added for FacilitiesForm
  // Staff information
  person_in_charge?: string;
  manager_name?: string;
  year_established?: number;
  // Room information
  max_residents?: number;
  max_occupancy?: number;
  room_sizes?: any; // Added for RoomsForm
  private_rooms_count?: number; // Added for RoomsForm
  shared_rooms_count?: number; // Added for RoomsForm
  ensuite_rooms_count?: number; // Added for RoomsForm
  deposit_required?: boolean; // Added for RoomsForm
  deposit_amount?: number | string; // Changed to accept both number and string to match form input handling
  // Other information
  hiqa_profile_url?: string;
  category?: string;
  claimed?: boolean;
  standardized_address?: string;
  specializations?: string[];
  wheelchair_accessible?: boolean;
  last_updated_at?: string;
}

export function useNursingHome(nhSlug: string | undefined) {
  console.log('useNursingHome hook called with nhSlug:', nhSlug);
  return useQuery({
    queryKey: ['nursing_home', nhSlug],
    queryFn: async (): Promise<NursingHome> => {
      try {
        const { data, error } = await supabase
          .from('nursing_homes')
          .select('*')
          .eq('nh_slug', nhSlug)
          .single();
        console.log('Supabase query result:', { data, error });
        
        if (error) {
          console.error('Supabase error:', error);
          toast.error(`Database error: ${error.message}`);
          throw error;
        }
        
        console.log(`Viewing nursing home: ${data?.center_name}`);
        
        return data as unknown as NursingHome;  // Using unknown to bypass type checking since we know the structure
      } catch (err) {
        console.error('Error fetching nursing home:', err);
        toast.error("Failed to load nursing home details. Please try again later.");
        throw err;
      }
    },
    enabled: !!nhSlug,
  });
}
